<template>
    <div
        v-if="visible"
        :style="popupStyle"
        class="popupArc"
        @click.stop
    >
        <q-bar class="bg-primary text-white q-mb-xs">
            <div class="text-subtitle1 text-weight-medium">
                {{ data.name }}
            </div>
            <q-space />
            <q-btn
                dense
                flat
                color="white"
                icon="close"
                @click.stop="handleClickClose"
            />
        </q-bar>
        <q-inner-loading
            v-if="isLoading"
            :showing="isLoading"
            :style="{ zIndex: 1000 }"
            visible
        />
        <div
            v-else
        >
            <div class="row justify-center q-mb-sm">
                <div class="row items-center q-pa-md">
                    <p style="font-size: medium; font-weight: bold">
                        {{ $t('visualization.popup.impact.title') }}
                    </p>
                </div>
                <div class="column items-center">
                    {{ $t('visualization.popup.impact.current') }}
                    <q-circular-progress
                        :value="currentWallaceCofPercent"
                        size="45px"
                        :thickness="0.2"
                        :color="severityColors[0]"
                        class="q-ma-md"
                        showValue
                    >
                        <div
                            class="q-mt-sm"
                            style="color: #000000; font-size: 14px;"
                        >
                            {{ currentWallaceCofPercent }}%
                        </div>
                        <q-tooltip>
                            {{ currentWallaceCofPercent }}{{ $t('visualization.popup.impact.currentImpact') }}
                        </q-tooltip>
                    </q-circular-progress>
                    {{ $t(`visualization.popup.impact.level.${severityLevels[0]}`) }}
                </div>
                <div class="column items-center">
                    {{ $t('visualization.popup.impact.total') }}
                    <q-circular-progress
                        :value="totalWallaceCofPercent"
                        size="45px"
                        :thickness="0.2"
                        :color="severityColors[1]"
                        class="q-ma-md"
                        showValue
                    >
                        <div
                            class="q-mt-sm"
                            style="color: #000000; font-size: 14px;"
                        >
                            {{ totalWallaceCofPercent }}%
                        </div>
                        <q-tooltip>
                            {{ totalWallaceCofPercent }}{{ $t('visualization.popup.impact.totalImpact') }}
                        </q-tooltip>
                    </q-circular-progress>
                    {{ $t(`visualization.popup.impact.level.${severityLevels[1]}`) }}
                </div>
            </div>
            <div
                class="buttons_container q-gutter-sm q-mx-sm"
            >
                <div class="button_row">
                    <q-btn
                        :disable="isContainFilterApplied"
                        icon="check_box"
                        :label="$t('visualization.popup.buttons.contain')"
                        class="q-mr-sm"
                        unelevated
                        noCaps
                        color="primary"
                        @click="handleAddFilter('TRACE_CONTAINS_ARCS')"
                    />
                    <span style="margin-left: 10px">{{ casesValue.contains }} cases</span>
                </div>
                <div class="button_row">
                    <q-btn
                        :disable="isNotContainFilterApplied"
                        icon="disabled_by_default"
                        :label="$t('visualization.popup.buttons.not_contain')"
                        class="q-mr-sm"
                        unelevated
                        noCaps
                        color="primary"
                        @click="handleAddFilter('TRACE_NOT_CONTAINS_ARCS')"
                    />
                    <span style="margin-left: 10px">{{ casesValue.notContains }} cases</span>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError,
} from '@/api'
import { filtersStorageMixin } from '@/mixins'

export default {
    name: 'ArcPopUp',
    mixins: [filtersStorageMixin],
    props: {
        data: VueTypes.object,
        visible: VueTypes.bool,
        position: VueTypes.object,
        tab: VueTypes.string,
    },
    emits: ['close', 'add-filter'],
    data() {
        return {
            isLoading: false,
            currentWallaceCofPercent: 0,
            totalWallaceCofPercent: 0,
            severityLevels: ["low", "low"],
            severityColors: ["green", "green"],
            casesValue: {
                contains: 0,
                notContains: 0,
            },
        }
    },
    computed: {
        popupStyle() {
            return {
                left: `${this.position.x}px`,
                top: `${this.position.y}px`,
            }
        },
        isContainFilterApplied() {
            const arcName = this.data?.name
            if (!arcName) return false
            const { visualizationFilters } = this
            return (visualizationFilters || []).some((filter) => {
                if (!filter || filter.id !== 'TRACE_CONTAINS_ARCS') return false

                // Canonical
                if (Array.isArray(filter.values) && filter.values.includes(arcName)) return true

                // Legacy fallbacks
                if (Array.isArray(filter.type?.arc) && filter.type.arc.includes(arcName)) return true
                if (Array.isArray(filter.value) && filter.value.includes(arcName)) return true
                if (filter.value === arcName) return true
                return false
            })
        },
        isNotContainFilterApplied() {
            const arcName = this.data?.name
            if (!arcName) return false
            const { visualizationFilters } = this
            return (visualizationFilters || []).some((filter) => {
                if (!filter || filter.id !== 'TRACE_NOT_CONTAINS_ARCS') return false

                // Canonical
                if (Array.isArray(filter.values) && filter.values.includes(arcName)) return true

                // Legacy fallbacks
                if (Array.isArray(filter.type?.arc) && filter.type.arc.includes(arcName)) return true
                if (Array.isArray(filter.value) && filter.value.includes(arcName)) return true
                if (filter.value === arcName) return true
                return false
            })
        },
    },
    watch: {
        tab() {
            if (this.visible) {
                this.handleClickClose()
            }
        },
        visible(newVal) {
            if (newVal === true) {
                this.getImpactFromWallaceCof()
                this.applyFilters()
            }
        },
        data() {
            this.getImpactFromWallaceCof()
            this.applyFilters()
        },
        currentWallaceCofPercent() {
            this.calculateSeverityLevel()
        },
        totalWallaceCofPercent() {
            this.calculateSeverityLevel()
        },
    },

    methods: {
        getImpactFromWallaceCof() {
            this.isLoading = true
            this.currentWallaceCofPercent = 0
            this.totalWallaceCofPercent = 0
            const { processId } = this.$route.params
            const { variantCount: variants, variantUid: variantsId, loopUid: loopId } = this.data?.modelData || {}
            const arcName = this.formatArcName(this.data.name)

            const params = this.buildVisualizationParams({
                ...(variants && { variants }),
                ...(variantsId && { variantsId }),
                ...(loopId && { loopId }),
            })
            Api().visualizations.arcImpactWallaceCof({ processId, arcName, params })
                .then((data) => {
                    const cw = data?.data?.data?.[0]?.currentCW
                    this.currentWallaceCofPercent = cw != null ? Math.round(cw * 100) : 0
                    const totalCw = data?.data?.data?.[0]?.totalCW
                    this.totalWallaceCofPercent = totalCw != null ? Math.round(totalCw * 100) : 0
                }).catch(notifyError)
                .finally(() => {
                    this.isLoading = false
                })
        },
        formatArcName(name) {
            return encodeURIComponent(name)
        },
        async applyFilters() {
            try {
                const filters = [
                    'TRACE_CONTAINS_ARCS',
                    'TRACE_NOT_CONTAINS_ARCS',
                ]

                const promises = filters.map((filter) => this.filterCases(filter))

                await Promise.all(promises)
                    .then((results) => {
                        this.casesValue = {
                            contains: results[0].cases,
                            notContains: results[1].cases,
                        }
                    })
            } catch (err) {
                notifyError(err)
            }
        },

        async filterCases(filter) {
            const { processId } = this.$route.params
            const modelData = this.data && this.data.modelData ? this.data.modelData : {}
            const { loopUid, variantCount, variantUid } = modelData
            const params = {
                filters: [
                    {
                        id: filter,
                        values: [this.data && this.data.name ? this.data.name : ''],
                    },
                ],
                filterSet: [],
                operator: "AND",
                ...(loopUid !== undefined ? { loopUid } : {}),
                ...(variantCount ? { variantCount } : {}),
                ...(variantUid ? { variantUid } : {}),
            }
            return apiRequest(Api().visualizations.modelInfo({ processId, params }))
        },

        getSeverity(percent) {
            if (percent <= 30) {
                return { level: "low", color: "green" }
            } if (percent > 30 && percent <= 60) {
                return { level: "medium", color: "orange" }
            }
            return { level: "high", color: "red" }
        },

        calculateSeverityLevel() {
            const currentSeverity = this.getSeverity(this.currentWallaceCofPercent)
            const totalSeverity = this.getSeverity(this.totalWallaceCofPercent)
            this.severityLevels[0] = currentSeverity.level
            this.severityColors[0] = currentSeverity.color
            this.severityLevels[1] = totalSeverity.level
            this.severityColors[1] = totalSeverity.color
        },

        formatFilter(selectedFilter) {
            return {
                id: selectedFilter,
                values: [this.data?.name].filter(Boolean),
                meta: {
                    arc: this.data?.name,
                },
            }
        },

        async handleAddFilter(selectedFilter) {
            this.$eventBus.emit('addFilter', this.formatFilter(selectedFilter))
        },

        handleClickClose() {
            this.$emit('close')
        },

    },
}
</script>
<style lang="scss">
     .popupArc {
        border: 1px solid $gray-05;
        border-radius: 5px;
        background: white;
        position: absolute;
        padding: '10px';
        overflow: hidden;
        min-width: max-content;
    }

    .buttons_container {
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 10px;
    }

    .button_row {
        display: flex;
        align-items: center;
        gap: 15px;
    }

    .button_row .q-btn {
        width: 170px;
    }
</style>
